import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import { AutocompleteProps as MuiAutocompleteProps } from "@mui/material/Autocomplete";

import Autocomplete from "components/DesignComponents/Autocomplete";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import {
    DROPDOWN_SIZES,
    Filter,
    filterDropdownCustomWidth,
    handleAutocompleteInputValueChange,
    RectangleLoader
} from "shared";

import AutocompleteListBox from "../components/AutocompleteListBox";
import { getFilterDropdownValueIds, getFilterOptionLabel } from "../functions";
import { useFilterDropdown } from "./hooks";
import { FilterDropdownProps } from "./types";

const FilterDropdown = ({
    filter,
    placeholder,
    customTableName,
    additionalParams,
    shouldIgnoreCompanySelector,
    containerCustomStyle,
    containerLoaderCustomStyle,
    ...props
}: FilterDropdownProps &
    Omit<
        MuiAutocompleteProps<any, boolean, boolean, boolean>,
        "renderInput" | "size" | "options"
    >) => {
    const { filteredValues: mainFilteredValues, setQueryParams } =
        useContext(ParamsContext);

    const {
        isInnerTable,
        innerTableFilteredValues,
        isInitialLoading,
        setInnerTableParams
    } = useContext(TableContext);

    const { t } = useTranslation();

    const { isOpen, isLoading, options, setIsOpen } = useFilterDropdown(
        filter,
        customTableName,
        additionalParams,
        shouldIgnoreCompanySelector
    );

    const [inputValue, setInputValue] = useState("");
    const [value, setValue] = useState<Filter[]>([]);

    const filteredValues = isInnerTable
        ? innerTableFilteredValues
        : mainFilteredValues;

    // Set value
    useEffect(() => {
        const actualFilter = filteredValues.find(
            filteredValue => filteredValue.filterName === filter
        );

        const newValue = actualFilter ? actualFilter.value : [];

        setValue(prevValue => {
            const isEqual =
                JSON.stringify(prevValue) === JSON.stringify(newValue);

            return isEqual ? prevValue : newValue;
        });
    }, [filteredValues, filter]);

    const handleChange = (newValue: Filter[]) => {
        const paramsPayload = {
            [filter]: getFilterDropdownValueIds(filter, newValue)
        };

        const filteredValuesPayload = {
            filterName: filter,
            value: newValue
        };

        (isInnerTable ? setInnerTableParams : setQueryParams)({
            params: paramsPayload,
            resetPage: true,
            filteredValues: filteredValuesPayload
        });
    };

    return isInitialLoading ? (
        <RectangleLoader
            customStyle={
                containerLoaderCustomStyle || filterDropdownCustomWidth
            }
            height={36}
        />
    ) : (
        <Autocomplete
            {...props}
            fullWidth
            multiple
            size={DROPDOWN_SIZES.Small}
            includeInputInList
            disableClearable
            disableCloseOnSelect
            open={isOpen}
            inputValue={inputValue}
            value={value}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            options={options}
            loading={isLoading}
            onInputChange={(_, newInputValue, reason) =>
                handleAutocompleteInputValueChange(reason, () =>
                    setInputValue(newInputValue)
                )
            }
            onChange={(_, newFilterValue) => handleChange(newFilterValue)}
            slots={{
                listbox: AutocompleteListBox
            }}
            renderValue={() => null}
            textFieldParams={{
                placeholder
            }}
            getOptionLabel={(option: Filter) =>
                getFilterOptionLabel(option, filter, t)
            }
            customStyle={containerCustomStyle}
            css={css(filterDropdownCustomWidth)}
        />
    );
};

export default FilterDropdown;
